/**
 * Panneau **« Effets de la run »** (U-03) — consultable à tout moment pendant une partie.
 *
 * Ce que le panneau affiche, et d'où cela vient (CDC §8, DECISIONS §6.8, §7.5, §8) :
 * - **cartes obtenues** : les définitions renvoyées par `POST /runs/:id/cards/choose` (carte
 *   choisie et carte bonus de boss), avec nom, élément, rareté et effets chiffrés publiés par le
 *   serveur ;
 * - **multiplicateurs cumulés** : `state.multiplicateurs` de la dernière réponse serveur, recopié
 *   tel quel — aucune somme n'est refaite ici, même si le total paraît différent de la somme des
 *   cartes affichées ;
 * - **jokers restants** : `state.jokers`, valeur d'autorité ;
 * - **synergies découvertes** : uniquement les cartes de synergie que le serveur a accordées. Les
 *   autres ne sont ni nommées ni comptées (F17 : « le serveur renvoie `decouverte: false` et le
 *   client n'affiche ni nom ni description »).
 *
 * Ergonomie : tiroir plein écran sur téléphone, panneau latéral sur écran large, fermeture
 * explicite (bouton traduit, touche Échap, appui hors du panneau), cibles ≥ 44 px. **La vague
 * n'est pas mise en pause** : c'est la décision par défaut de la carte, et le panneau le dit au
 * joueur pendant une vague pour qu'il ne perde pas de PV sans le savoir.
 */
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { cleTraductionContenu } from '../i18n';
import { IconeElement } from './IconeElement';
import { resumeEffet } from './PanneauCartes';
import {
  CLES_MULTIPLICATEURS,
  construireVueEffets,
  estEffetSynergie,
  type CarteRunVue,
  type CleMultiplicateur,
  type ElementCarte,
} from '../jeu/effets';

import type { CartePublique, Rarete, RunState } from '@elemora/shared/api';
import type { TFunction } from 'i18next';
import type { CSSProperties } from 'react';

/** Libellé d'élément : clés statiques, aucune concaténation à l'exécution. */
const CLE_ELEMENT: Readonly<Record<ElementCarte, string>> = {
  feu: 'content:element.feu.name',
  eau: 'content:element.eau.name',
  terre: 'content:element.terre.name',
  air: 'content:element.air.name',
  neutre: 'jeu.effets.element.neutre',
};

/** Libellé de rareté (`content:rarity.<rarete>.name`). */
const CLE_RARETE: Readonly<Record<Rarete, string>> = {
  commune: 'content:rarity.commune.name',
  rare: 'content:rarity.rare.name',
  legendaire: 'content:rarity.legendaire.name',
  mythique: 'content:rarity.mythique.name',
};

/** Libellé traduit d'une statistique (`jeu.effets.multiplicateurs.<clé>`). */
function cleStatistique(cle: CleMultiplicateur): string {
  return `jeu.effets.multiplicateurs.${cle}`;
}

/**
 * Pourcentage signé, localisé (`Intl`), avec au plus deux décimales.
 *
 * Le signe `+` est ajouté pour les valeurs positives : les multiplicateurs du serveur sont des
 * sommes de pourcentages, pas des facteurs (DECISIONS §6.8). La valeur affichée est **celle du
 * serveur**, jamais un total recalculé à partir des cartes.
 */
export function formaterPourcentage(valeur: number, langue: string): string {
  const texte = new Intl.NumberFormat(langue.length > 0 ? langue : 'fr', {
    maximumFractionDigits: 2,
  }).format(valeur);
  return `${valeur > 0 ? '+' : ''}${texte} %`;
}

/** Libellé d'élément d'une carte, y compris `neutre` (absent du catalogue de contenu). */
function libelleElement(element: ElementCarte, t: TFunction): string {
  return t(CLE_ELEMENT[element]);
}

/**
 * Libellé d'un identifiant d'élément **publié par le contenu** (`parElement`).
 *
 * Un identifiant inconnu est affiché tel quel : c'est une donnée serveur, jamais un texte
 * d'interface inventé. Les quatre éléments et `neutre` passent par le catalogue traduit.
 */
export function libelleElementPublie(element: string, t: TFunction): string {
  if (
    element === 'feu' ||
    element === 'eau' ||
    element === 'terre' ||
    element === 'air' ||
    element === 'neutre'
  ) {
    return t(CLE_ELEMENT[element]);
  }
  return element;
}

/** Libellé de rareté. */
function libelleRarete(rarete: Rarete, t: TFunction): string {
  return t(CLE_RARETE[rarete]);
}

/**
 * Résumé lisible d'un effet **tel que publié par le serveur**.
 *
 * Les cinq formes simples sont déléguées à `resumeEffet` (même vocabulaire que l'écran de choix de
 * carte et le grimoire). La synergie, elle, est détaillée ici : sa condition (`elements`,
 * `minTours`) et l'effet qu'elle accorde ne doivent pas être réduits au mot « Synergie ».
 */
export function resumeEffetRun(effet: unknown, t: TFunction, langue: string): string {
  if (!estEffetSynergie(effet)) return resumeEffet(effet, t);

  const champs = effet as Record<string, unknown>;
  const condition = (
    typeof champs['condition'] === 'object' && champs['condition'] !== null
      ? champs['condition']
      : {}
  ) as Record<string, unknown>;
  const elements = Array.isArray(condition['elements'])
    ? condition['elements'].map((element) => String(element))
    : [];
  const noms = elements.map((element) => libelleElementPublie(element, t)).join(' + ');
  const minTours = typeof condition['minTours'] === 'number' ? condition['minTours'] : null;
  const conditionTexte =
    minTours === null
      ? t('jeu.effets.synergies.condition', { elements: noms })
      : t('jeu.effets.synergies.conditionTours', {
          elements: noms,
          nombre: new Intl.NumberFormat(langue.length > 0 ? langue : 'fr').format(minTours),
        });
  const cible = resumeEffet(champs['effet'], t);
  return t('jeu.effets.synergies.resume', { condition: conditionTexte, effet: cible });
}

/** Une carte obtenue, avec ses effets chiffrés. */
function LigneCarte({
  carte,
  t,
  langue,
}: {
  readonly carte: CarteRunVue;
  readonly t: TFunction;
  readonly langue: string;
}) {
  const nom = t(cleTraductionContenu(carte.nameKey));
  const couleur =
    carte.element === 'neutre' ? 'var(--elemora-accent)' : `var(--element-${carte.element})`;
  return (
    <li
      className="effets-carte"
      data-carte-run={carte.cardId}
      data-element={carte.element}
      data-rarete={carte.rarete}
      style={{ '--couleur-element': couleur } as CSSProperties}
    >
      <span className="effets-carte__entete">
        {carte.element === 'neutre' ? null : <IconeElement element={carte.element} />}
        <span className="effets-carte__nom">{nom}</span>
        <span className="effets-carte__rarete">{libelleRarete(carte.rarete, t)}</span>
      </span>
      <span className="effets-carte__meta">
        {libelleElement(carte.element, t)}
        {carte.synergie ? ` · ${t('jeu.effets.synergies.etiquette')}` : null}
      </span>
      {carte.effets === null || carte.effets.length === 0 ? (
        <span className="effets-carte__effet effets-carte__effet--masque">
          {t('jeu.effets.cartes.effetNonPublie')}
        </span>
      ) : (
        <ul className="effets-carte__effets">
          {carte.effets.map((effet, rang) => (
            <li key={`${carte.cardId}-${String(rang)}`} data-effet={rang}>
              {resumeEffetRun(effet, t, langue)}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

export interface ProprietesPanneauEffetsRun {
  /** État d'autorité de la run (jokers et multiplicateurs sont recopiés tels quels). */
  readonly run: Pick<RunState, 'jokers' | 'multiplicateurs'>;
  /** Cartes accordées par le serveur pendant cette run, dans l'ordre d'obtention. */
  readonly cartes: readonly CartePublique[];
  /** Vrai pendant une vague : le panneau rappelle alors que la partie ne s'arrête pas. */
  readonly vagueEnCours: boolean;
  readonly onFermer: () => void;
}

export function PanneauEffetsRun({
  run,
  cartes,
  vagueEnCours,
  onFermer,
}: ProprietesPanneauEffetsRun) {
  const { t, i18n } = useTranslation();
  const langue = i18n.language;
  const boutonFermer = useRef<HTMLButtonElement>(null);
  const vue = construireVueEffets(run, cartes);

  // Le focus entre dans le panneau à l'ouverture : au clavier, on ne reste pas sur le plateau.
  useEffect(() => {
    boutonFermer.current?.focus();
  }, []);

  // Échap ferme le panneau, quel que soit l'élément qui a le focus.
  useEffect(() => {
    const surTouche = (evenement: KeyboardEvent): void => {
      if (evenement.key === 'Escape') {
        evenement.stopPropagation();
        onFermer();
      }
    };
    document.addEventListener('keydown', surTouche);
    return () => {
      document.removeEventListener('keydown', surTouche);
    };
  }, [onFermer]);

  return (
    <div className="panneau-effets" data-panneau-effets="ouvert">
      {/* Voile décoratif : il ferme au clic, mais le bouton « Fermer » reste la voie accessible. */}
      <div
        className="panneau-effets__voile"
        aria-hidden="true"
        data-fermer-voile="oui"
        onClick={onFermer}
      />
      <section
        className="panneau-effets__tiroir"
        role="dialog"
        aria-modal="false"
        aria-labelledby="titre-effets-run"
        data-effets-run="panneau"
      >
        <header className="panneau-effets__entete">
          <div className="panneau-effets__titres">
            <h2 className="panneau-effets__titre" id="titre-effets-run">
              {t('jeu.effets.titre')}
            </h2>
            <p className="panneau-effets__sousTitre">{t('jeu.effets.sousTitre')}</p>
          </div>
          <button
            type="button"
            className="bouton bouton--secondaire panneau-effets__fermer"
            ref={boutonFermer}
            data-fermer-effets="oui"
            aria-label={t('jeu.effets.fermer')}
            onClick={onFermer}
          >
            <span className="bouton__libelle">{t('jeu.effets.fermer')}</span>
          </button>
        </header>

        {vagueEnCours ? (
          <p className="note note--alerte panneau-effets__alerte" data-effets-vague-continue="oui">
            {t('jeu.effets.vagueContinue')}
          </p>
        ) : null}

        <div className="panneau-effets__corps">
          <section
            className="effets-section"
            aria-labelledby="effets-cartes-titre"
            data-section="cartes"
          >
            <h3 className="effets-section__titre" id="effets-cartes-titre">
              {t('jeu.effets.cartes.titre')}
            </h3>
            <p className="note" data-effets-nombre-cartes={vue.cartes.length}>
              {t('jeu.effets.cartes.valeur', { count: vue.cartes.length })}
            </p>
            {vue.cartes.length === 0 ? (
              <p className="effets-vide" data-effets-cartes-vide="oui">
                {t('jeu.effets.cartes.vide')}
              </p>
            ) : (
              <ul className="effets-cartes">
                {vue.cartes.map((carte) => (
                  <LigneCarte key={carte.cardId} carte={carte} t={t} langue={langue} />
                ))}
              </ul>
            )}
            {vue.cartesSansEffetPublie === 0 ? null : (
              <p className="note" data-effets-sans-publie={vue.cartesSansEffetPublie}>
                {t('jeu.effets.cartes.aideSansPublie')}
              </p>
            )}
          </section>

          <section
            className="effets-section"
            aria-labelledby="effets-mult-titre"
            data-section="multiplicateurs"
          >
            <h3 className="effets-section__titre" id="effets-mult-titre">
              {t('jeu.effets.multiplicateurs.titre')}
            </h3>
            <p className="note">{t('jeu.effets.multiplicateurs.aide')}</p>
            <ul className="effets-mult">
              {CLES_MULTIPLICATEURS.map((cle) => (
                <li
                  className="effets-mult__ligne"
                  key={cle}
                  data-mult={cle}
                  data-valeur={vue.multiplicateurs.parStat[cle]}
                >
                  <span className="effets-mult__nom">{t(cleStatistique(cle))}</span>
                  <span
                    className={`effets-mult__valeur${
                      vue.multiplicateurs.parStat[cle] === 0 ? ' effets-mult__valeur--zero' : ''
                    }`}
                  >
                    {formaterPourcentage(vue.multiplicateurs.parStat[cle], langue)}
                  </span>
                </li>
              ))}
              <li
                className="effets-mult__ligne"
                data-mult="gainEclats"
                data-valeur={vue.multiplicateurs.gainEclats}
              >
                <span className="effets-mult__nom">
                  {t('jeu.effets.multiplicateurs.gainEclats')}
                </span>
                <span
                  className={`effets-mult__valeur${
                    vue.multiplicateurs.gainEclats === 0 ? ' effets-mult__valeur--zero' : ''
                  }`}
                >
                  {formaterPourcentage(vue.multiplicateurs.gainEclats, langue)}
                </span>
              </li>
            </ul>

            <h4 className="effets-section__sousTitre">
              {t('jeu.effets.multiplicateurs.parElement')}
            </h4>
            <p className="note">{t('jeu.effets.multiplicateurs.parElementAide')}</p>
            {vue.multiplicateurs.parElement.length === 0 ? (
              <p className="effets-vide" data-effets-elements-vide="oui">
                {t('jeu.effets.multiplicateurs.aucunElement')}
              </p>
            ) : (
              <ul className="effets-elements">
                {vue.multiplicateurs.parElement.map((element) => (
                  <li
                    className="effets-element"
                    key={element.element}
                    data-mult-element={element.element}
                  >
                    <span className="effets-element__nom">
                      {libelleElementPublie(element.element, t)}
                    </span>
                    <dl className="effets-element__valeurs">
                      {(['degats', 'portee', 'cadenceMs', 'vitesseProjectile'] as const).map(
                        (cle) => (
                          <div className="effets-element__valeur" key={cle}>
                            <dt>{t(cleStatistique(cle))}</dt>
                            <dd data-valeur={element[cle]}>
                              {formaterPourcentage(element[cle], langue)}
                            </dd>
                          </div>
                        ),
                      )}
                    </dl>
                  </li>
                ))}
              </ul>
            )}
            {vue.multiplicateurs.elementsSansBonus === 0 ? null : (
              <p
                className="note"
                data-effets-elements-sans-bonus={vue.multiplicateurs.elementsSansBonus}
              >
                {t('jeu.effets.multiplicateurs.autresElements')}
              </p>
            )}
          </section>

          <section
            className="effets-section"
            aria-labelledby="effets-jokers-titre"
            data-section="jokers"
          >
            <h3 className="effets-section__titre" id="effets-jokers-titre">
              {t('jeu.effets.jokers.titre')}
            </h3>
            <p className="effets-jokers__valeur" data-jokers={vue.jokers}>
              {new Intl.NumberFormat(langue.length > 0 ? langue : 'fr').format(vue.jokers)}
            </p>
            <p className="note">{t('jeu.effets.jokers.aide')}</p>
          </section>

          <section
            className="effets-section"
            aria-labelledby="effets-synergies-titre"
            data-section="synergies"
          >
            <h3 className="effets-section__titre" id="effets-synergies-titre">
              {t('jeu.effets.synergies.titre')}
            </h3>
            <p className="note" data-effets-nombre-synergies={vue.synergies.length}>
              {t('jeu.effets.synergies.aide')}
            </p>
            {vue.synergies.length === 0 ? (
              <p className="effets-vide" data-effets-synergies-vide="oui">
                {t('jeu.effets.synergies.vide')}
              </p>
            ) : (
              <ul className="effets-synergies">
                {vue.synergies.map((carte) => (
                  <li className="effets-synergie" key={carte.cardId} data-synergie={carte.cardId}>
                    <span className="effets-synergie__nom">
                      {t(cleTraductionContenu(carte.nameKey))}
                    </span>
                    <span className="effets-synergie__element">
                      {libelleElement(carte.element, t)} · {libelleRarete(carte.rarete, t)}
                    </span>
                    <ul className="effets-synergie__effets">
                      {(carte.effets ?? []).map((effet, rang) => (
                        <li key={`${carte.cardId}-synergie-${String(rang)}`}>
                          {resumeEffetRun(effet, t, langue)}
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            )}
            <p className="note">{t('jeu.effets.synergies.masquees')}</p>
          </section>
        </div>
      </section>
    </div>
  );
}
